/**
 * Build Status Tracker
 * 
 * Manages build status tracking in localStorage with the structure:
 * {
 *   [project_id]: {
 *     [feature_id]: {
 *       feature_id: string (UIID),
 *       buildDetails: [
 *         {
 *           build_initiated_at: string (ISO timestamp),
 *           build_status: string,
 *           git_commit: string | null,
 *           progressUpdates: [
 *             {
 *               id: string,
 *               type: 'status' | 'error',
 *               content: string,
 *               agent?: string,
 *               llm?: string,
 *               progress?: number | null,
 *               timestamp: string (ISO timestamp)
 *             }
 *           ]
 *         }
 *       ]
 *     }
 *   }
 * }
 */

import { getFromStorage, saveToStorage } from './storageUtils';
import type { MessageCategory } from '../../features/dashboard/channels/build_feature/components/BuildProgressTab';
import { inferToolUsageKindFromContent } from '../../features/dashboard/channels/build_feature/components/ai-activity-utils';

const STORAGE_KEY = 'trackBuildStatus';
/** Only the latest N AI lines are persisted; totals stay correct via `aiActivitySummary` on the build. */
const MAX_STORED_AI_MESSAGES = 20;
const AI_CATEGORIES: Set<string> = new Set(['ai_thinking', 'ai_tool_usage', 'ai_text']);

/**
 * Running totals for AI lines for a single build (survives truncation of progressUpdates).
 */
export interface AiActivitySummary {
    thinking: number;
    aiText: number;
    toolByKind: Record<string, number>;
}

function emptyAiActivitySummary(): AiActivitySummary {
    return { thinking: 0, aiText: 0, toolByKind: {} };
}

function applyAiActivitySummaryIncrement(
    buildDetail: BuildDetail,
    update: StoredProgressUpdate
): void {
    if (!update.category || !AI_CATEGORIES.has(update.category)) {
        return;
    }
    if (!buildDetail.aiActivitySummary) {
        buildDetail.aiActivitySummary = emptyAiActivitySummary();
    }
    const s = buildDetail.aiActivitySummary;
    if (update.category === 'ai_thinking') {
        s.thinking += 1;
    } else if (update.category === 'ai_text') {
        s.aiText += 1;
    } else if (update.category === 'ai_tool_usage') {
        const kind = update.toolUsageKind || inferToolUsageKindFromContent(update.content);
        s.toolByKind[kind] = (s.toolByKind[kind] || 0) + 1;
    }
}

/**
 * Progress update stored in localStorage (timestamp is ISO string for serialization)
 */
export interface StoredProgressUpdate {
    id: string;
    type: 'status' | 'error';
    content: string;
    agent?: string;
    llm?: string;
    progress?: number | null;
    timestamp: string; // ISO string for storage
    uiid?: string;
    featureName?: string;
    backendId?: string;
    backendTs?: string;
    category?: MessageCategory;
    toolUsageKind?: string;
}

/**
 * Progress update with Date object (for use in components)
 */
export interface ProgressUpdate {
    id: string;
    type: 'status' | 'error';
    content: string;
    agent?: string;
    llm?: string;
    progress?: number | null;
    timestamp: Date;
    uiid?: string;
    featureName?: string;
    backendId?: string;
    backendTs?: string;
    category?: MessageCategory;
    toolUsageKind?: string;
}

export interface BuildDetail {
    build_initiated_at: string;
    build_status: string;
    git_commit: string | null;
    progressUpdates?: StoredProgressUpdate[]; // Optional for backward compatibility
    /** Cumulative AI counts for this build (not reduced when progressUpdates AI rows are trimmed) */
    aiActivitySummary?: AiActivitySummary;
}

export interface FeatureBuildStatus {
    feature_id: string; // UIID
    buildDetails: BuildDetail[];
}

export interface ProjectBuildStatus {
    [feature_id: string]: FeatureBuildStatus;
}

export interface TrackBuildStatus {
    [project_id: string]: ProjectBuildStatus;
}

/**
 * Strip legacy build_number from build details (migration for existing localStorage data)
 */
function normalizeBuildDetails(buildDetails: (BuildDetail & { build_number?: number })[]): BuildDetail[] {
    return buildDetails.map(({ build_number: _removed, ...rest }) => rest as BuildDetail);
}

/**
 * Get all build status tracking data (normalized: build_number removed if present)
 */
export function getBuildStatusTracking(): TrackBuildStatus {
    const raw = getFromStorage<TrackBuildStatus>(STORAGE_KEY) || {};
    const normalized: TrackBuildStatus = {};
    for (const projectId in raw) {
        const project = raw[projectId];
        normalized[projectId] = {};
        for (const featureId in project) {
            const feature = project[featureId];
            if (feature?.buildDetails) {
                normalized[projectId][featureId] = {
                    ...feature,
                    buildDetails: normalizeBuildDetails(feature.buildDetails)
                };
            } else {
                normalized[projectId][featureId] = feature;
            }
        }
    }
    return normalized;
}

/**
 * Get build status for a specific project
 */
export function getProjectBuildStatus(projectId: string | number): ProjectBuildStatus | null {
    const tracking = getBuildStatusTracking();
    const projectKey = String(projectId);
    return tracking[projectKey] || null;
}

/**
 * Get build status for a specific feature (by UIID) within a project
 */
export function getFeatureBuildStatus(projectId: string | number, uiid: string): FeatureBuildStatus | null {
    const projectStatus = getProjectBuildStatus(projectId);
    if (!projectStatus) {
        return null;
    }
    return projectStatus[uiid] || null;
}

/**
 * Add a new build entry for a feature within a project
 */
export function addBuildEntry(
    projectId: string | number,
    uiid: string,
    buildStatus: string = 'In Progress',
    gitCommit: string | null = null
): void {
    const tracking = getBuildStatusTracking();
    const projectKey = String(projectId);
    
    // Get or create project entry
    let projectEntry = tracking[projectKey];
    if (!projectEntry) {
        projectEntry = {};
    }
    
    // Get or create feature entry
    let featureEntry = projectEntry[uiid];
    if (!featureEntry) {
        featureEntry = {
            feature_id: uiid,
            buildDetails: []
        };
    }
    
    // Create new build detail (build_initiated_at is the unique identifier)
    const newBuildDetail: BuildDetail = {
        build_initiated_at: new Date().toISOString(),
        build_status: buildStatus,
        git_commit: gitCommit,
        progressUpdates: []
    };
    
    // Add to buildDetails array
    featureEntry.buildDetails.push(newBuildDetail);
    
    // Update tracking
    projectEntry[uiid] = featureEntry;
    tracking[projectKey] = projectEntry;
    
    // Save to localStorage
    saveToStorage(STORAGE_KEY, tracking);
    
    // Dispatch custom event to notify components of change
    window.dispatchEvent(new Event('buildStatusChanged'));
}

/**
 * Update build status for a specific build within a project (identified by build_initiated_at)
 */
export function updateBuildStatus(
    projectId: string | number,
    uiid: string,
    buildInitiatedAt: string,
    newStatus: string,
    gitCommit?: string | null
): void {
    const tracking = getBuildStatusTracking();
    const projectKey = String(projectId);
    const projectEntry = tracking[projectKey];
    
    if (!projectEntry) {
        console.warn(`⚠️ Project ${projectId} not found in build tracking`);
        return;
    }
    
    const featureEntry = projectEntry[uiid];
    if (!featureEntry) {
        console.warn(`⚠️ Feature ${uiid} not found in project ${projectId}`);
        return;
    }
    
    const buildDetail = featureEntry.buildDetails.find(b => b.build_initiated_at === buildInitiatedAt);
    if (!buildDetail) {
        console.warn(`⚠️ Build not found for feature ${uiid} in project ${projectId} (initiated at ${buildInitiatedAt})`);
        return;
    }
    
    // Update status
    buildDetail.build_status = newStatus;
    
    // Update git commit if provided
    if (gitCommit !== undefined) {
        buildDetail.git_commit = gitCommit;
    }
    
    // Save to localStorage
    saveToStorage(STORAGE_KEY, tracking);
    
    // Dispatch custom event to notify components of change
    window.dispatchEvent(new Event('buildStatusChanged'));
    
    console.log(`✅ Updated build status for feature ${uiid} in project ${projectId} to ${newStatus}`);
}

/**
 * Get the latest build for a feature within a project
 */
export function getLatestBuild(projectId: string | number, uiid: string): BuildDetail | null {
    const featureEntry = getFeatureBuildStatus(projectId, uiid);
    if (!featureEntry || featureEntry.buildDetails.length === 0) {
        return null;
    }
    
    // Sort by build_initiated_at descending and return the latest
    const sortedBuilds = [...featureEntry.buildDetails].sort(
        (a, b) => (b.build_initiated_at > a.build_initiated_at ? 1 : -1)
    );
    return sortedBuilds[0];
}

/**
 * Get all build history for a feature within a project (sorted by build_initiated_at descending)
 */
export function getBuildHistory(projectId: string | number, uiid: string): BuildDetail[] {
    const featureEntry = getFeatureBuildStatus(projectId, uiid);
    if (!featureEntry) {
        return [];
    }
    
    // Sort by build_initiated_at descending (newest first)
    return [...featureEntry.buildDetails].sort(
        (a, b) => (b.build_initiated_at > a.build_initiated_at ? 1 : -1)
    );
}

/**
 * Check if any feature in a project has a build in "In Progress" stage
 * @param projectId - The project ID to check
 * @returns true if any feature in the project has at least one build with status "In Progress", false otherwise
 */
export function hasAnyInProgressBuild(projectId: string | number): boolean {
    const projectStatus = getProjectBuildStatus(projectId);
    if (!projectStatus) {
        return false;
    }
    
    for (const featureId in projectStatus) {
        const featureEntry = projectStatus[featureId];
        if (featureEntry && featureEntry.buildDetails) {
            const hasInProgress = featureEntry.buildDetails.some(
                build => build.build_status === 'In Progress'
            );
            if (hasInProgress) {
                return true;
            }
        }
    }
    
    return false;
}

/**
 * Get all features in a project that currently have builds in "In Progress" stage
 * @param projectId - The project ID to check
 * @returns Array of feature IDs (UIIDs) that have at least one "In Progress" build
 */
export function getInProgressFeatures(projectId: string | number): string[] {
    const projectStatus = getProjectBuildStatus(projectId);
    if (!projectStatus) {
        return [];
    }
    
    const inProgressFeatures: string[] = [];
    
    for (const featureId in projectStatus) {
        const featureEntry = projectStatus[featureId];
        if (featureEntry && featureEntry.buildDetails) {
            const hasInProgress = featureEntry.buildDetails.some(
                build => build.build_status === 'In Progress'
            );
            if (hasInProgress) {
                inProgressFeatures.push(featureId);
            }
        }
    }
    
    return inProgressFeatures;
}

/**
 * Get all in-progress builds across ALL projects in localStorage
 * Useful when the project ID format might not match (e.g., numeric vs UUID)
 * @returns Array of objects containing projectId, featureId, and buildDetails
 */
export function getAllInProgressBuildsAcrossProjects(): Array<{
    projectId: string;
    featureId: string;
    latestBuild: BuildDetail;
    progressUpdates: ProgressUpdate[];
}> {
    const tracking = getBuildStatusTracking();
    const results: Array<{
        projectId: string;
        featureId: string;
        latestBuild: BuildDetail;
        progressUpdates: ProgressUpdate[];
    }> = [];
    
    for (const projectId in tracking) {
        const projectStatus = tracking[projectId];
        if (!projectStatus) continue;
        
        for (const featureId in projectStatus) {
            const featureEntry = projectStatus[featureId];
            if (!featureEntry || !featureEntry.buildDetails) continue;
            
            // Find the latest in-progress build
            const inProgressBuilds = featureEntry.buildDetails.filter(
                build => build.build_status === 'In Progress'
            );
            
            if (inProgressBuilds.length > 0) {
                // Get the latest in-progress build
                const latestBuild = inProgressBuilds.sort(
                    (a, b) => (b.build_initiated_at > a.build_initiated_at ? 1 : -1)
                )[0];
                
                // Convert stored progress updates to ProgressUpdate with Date objects
                const progressUpdates: ProgressUpdate[] = (latestBuild.progressUpdates || []).map(stored => ({
                    id: stored.id,
                    type: stored.type,
                    content: stored.content,
                    agent: stored.agent,
                    llm: stored.llm,
                    progress: stored.progress,
                    timestamp: new Date(stored.timestamp),
                    uiid: stored.uiid,
                    featureName: stored.featureName,
                    backendId: stored.backendId,
                    backendTs: stored.backendTs,
                    category: stored.category,
                    toolUsageKind: stored.toolUsageKind
                }));
                
                results.push({
                    projectId,
                    featureId,
                    latestBuild,
                    progressUpdates
                });
            }
        }
    }
    
    return results;
}

/**
 * Debug helper: Log all build tracking data structure
 */
export function debugBuildTracking(): void {
    const tracking = getBuildStatusTracking();
    console.log('🔍 [BuildStatusTracker] Full localStorage data:', tracking);
    console.log('🔍 [BuildStatusTracker] Project keys:', Object.keys(tracking));
    
    for (const projectId in tracking) {
        const projectStatus = tracking[projectId];
        console.log(`🔍 [BuildStatusTracker] Project ${projectId}:`, {
            featureCount: Object.keys(projectStatus || {}).length,
            features: Object.keys(projectStatus || {})
        });
    }
}

/**
 * Add a progress update to a specific build within a project (identified by build_initiated_at)
 */
export function addProgressUpdate(
    projectId: string | number,
    uiid: string,
    buildInitiatedAt: string,
    progressUpdate: Omit<ProgressUpdate, 'timestamp'> & { timestamp: Date | string }
): void {
    const tracking = getBuildStatusTracking();
    const projectKey = String(projectId);
    const projectEntry = tracking[projectKey];
    
    if (!projectEntry) {
        console.warn(`⚠️ Project ${projectId} not found in build tracking`);
        return;
    }
    
    const featureEntry = projectEntry[uiid];
    if (!featureEntry) {
        console.warn(`⚠️ Feature ${uiid} not found in project ${projectId}`);
        return;
    }
    
    const buildDetail = featureEntry.buildDetails.find(b => b.build_initiated_at === buildInitiatedAt);
    if (!buildDetail) {
        console.warn(`⚠️ Build not found for feature ${uiid} in project ${projectId} (initiated at ${buildInitiatedAt})`);
        return;
    }
    
    // Ensure progressUpdates array exists (for backward compatibility with old data)
    if (!buildDetail.progressUpdates) {
        buildDetail.progressUpdates = [];
    }
    
    // Check if progress update with same backendId already exists (skip if duplicate)
    if (progressUpdate.backendId) {
        const existingUpdate = buildDetail.progressUpdates.find(
            update => update.backendId === progressUpdate.backendId
        );
        if (existingUpdate) {
            console.log(`⏭️ Skipping duplicate progress update with backendId: ${progressUpdate.backendId}`);
            return; // Skip adding this update
        }
    }
    
    // Convert timestamp to ISO string if it's a Date object
    const timestampISO = typeof progressUpdate.timestamp === 'string' 
        ? progressUpdate.timestamp 
        : progressUpdate.timestamp.toISOString();
    
    const storedUpdate: StoredProgressUpdate = {
        id: progressUpdate.id,
        type: progressUpdate.type,
        content: progressUpdate.content,
        agent: progressUpdate.agent,
        llm: progressUpdate.llm,
        progress: progressUpdate.progress,
        timestamp: timestampISO,
        uiid: progressUpdate.uiid,
        featureName: progressUpdate.featureName,
        backendId: progressUpdate.backendId,
        backendTs: progressUpdate.backendTs,
        category: progressUpdate.category,
        toolUsageKind: progressUpdate.toolUsageKind
    };
    
    buildDetail.progressUpdates.push(storedUpdate);
    applyAiActivitySummaryIncrement(buildDetail, storedUpdate);

    // Keep only the last N AI activity messages to avoid bloating localStorage
    if (storedUpdate.category && AI_CATEGORIES.has(storedUpdate.category)) {
        const aiIndices: number[] = [];
        for (let i = 0; i < buildDetail.progressUpdates.length; i++) {
            const cat = buildDetail.progressUpdates[i].category;
            if (cat && AI_CATEGORIES.has(cat)) {
                aiIndices.push(i);
            }
        }
        if (aiIndices.length > MAX_STORED_AI_MESSAGES) {
            const toRemove = aiIndices.slice(0, aiIndices.length - MAX_STORED_AI_MESSAGES);
            buildDetail.progressUpdates = buildDetail.progressUpdates.filter(
                (_, idx) => !toRemove.includes(idx)
            );
        }
    }

    saveToStorage(STORAGE_KEY, tracking);
    
    // Dispatch custom event to notify components of change
    window.dispatchEvent(new Event('buildStatusChanged'));
}

/**
 * Get all progress updates for a specific build within a project (identified by build_initiated_at)
 * @returns Array of ProgressUpdate objects sorted by timestamp descending (newest first)
 */
export function getProgressUpdates(projectId: string | number, uiid: string, buildInitiatedAt: string): ProgressUpdate[] {
    const tracking = getBuildStatusTracking();
    const projectKey = String(projectId);
    const projectEntry = tracking[projectKey];
    
    if (!projectEntry) {
        return [];
    }
    
    const featureEntry = projectEntry[uiid];
    if (!featureEntry) {
        return [];
    }
    
    const buildDetail = featureEntry.buildDetails.find(b => b.build_initiated_at === buildInitiatedAt);
    if (!buildDetail || !buildDetail.progressUpdates) {
        return [];
    }
    
    // Convert stored updates to ProgressUpdate objects with Date timestamps
    const progressUpdates: ProgressUpdate[] = buildDetail.progressUpdates.map(stored => ({
        id: stored.id,
        type: stored.type,
        content: stored.content,
        agent: stored.agent,
        llm: stored.llm,
        progress: stored.progress,
        timestamp: new Date(stored.timestamp),
        uiid: stored.uiid,
        featureName: stored.featureName,
        backendId: stored.backendId,
        backendTs: stored.backendTs,
        category: stored.category,
        toolUsageKind: stored.toolUsageKind
    }));
    
    // Sort by timestamp descending (newest first)
    return progressUpdates.sort((a, b) => b.timestamp.getTime() - a.timestamp.getTime());
}

/**
 * Cumulative AI activity totals for the latest build of a feature (from localStorage).
 */
export function getAiActivitySummaryForLatestBuild(
    projectId: string | number,
    uiid: string
): AiActivitySummary | null {
    const latest = getLatestBuild(projectId, uiid);
    return latest?.aiActivitySummary ?? null;
}

/**
 * Get all progress updates for the latest build of a feature within a project
 * @returns Array of ProgressUpdate objects sorted by timestamp descending (newest first)
 */
export function getLatestBuildProgressUpdates(projectId: string | number, uiid: string): ProgressUpdate[] {
    const latestBuild = getLatestBuild(projectId, uiid);
    if (!latestBuild) {
        return [];
    }
    
    return getProgressUpdates(projectId, uiid, latestBuild.build_initiated_at);
}

/**
 * Clear all build tracking data (useful for testing/reset)
 */
export function clearBuildTracking(): void {
    localStorage.removeItem(STORAGE_KEY);
    console.log('🗑️ Cleared all build tracking data');
}
